// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updateShopByLookProduct from "@bg/App/Handlers/FindProductByErp/UpdateShopByLookPageProduct";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { ProductAvailabilityStyles, productAvailabilityStyles } from "@bg/Overrides/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@bg/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@bg/Overrides/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@bg/Overrides/Components/ProductPartNumbers";
import { ProductPriceStyles, productPriceStyles } from "@bg/Overrides/Components/ProductPrice";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import ProductAddToCartButton, { AddToCartButtonStyles } from "@bgs/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@bgs/Overrides/Components/ProductAddToListLink";
import ProductQuantityOrdered from "@bgs/Overrides/Components/ProductQuantityOrdered";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    productInfo: ownProps.fromShopByLook
        ? state.shopByLook.productsInfo[ownProps.product.productNumber]
        : getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    updateShopByLookProduct,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    hideAvailability?: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    customActionsTemplate?: (product: ProductModel) => React.ReactNode;
    fromShopByLook?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productItem?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    priceStlye?: ProductPriceStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: InjectableCss;
    qtyGridItem?: InjectableCss;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: InjectableCss;
    productAddToCartButtonStyles?: AddToCartButtonStyles;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productAddToListLinkStylesHovered?: ProductAddToListLinkStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    customActionStyle?: InjectableCss;
    cartLineOrderedQtyWrapper?: InjectableCss;
    productAddToListLinkWrapper?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

const getProductAddToListLinkStyles = (hovered = false): ProductAddToListLinkStyles => {
    const iconSrc = hovered ? "ProductListFilled" : "ProductListStroked";
    return {
        link: {
            css: css`
                & > span:last-child {
                    display: none;
                }
            `,
            icon: {
                iconProps: {
                    src: iconSrc,
                    size: 20,
                    color: "tertiary.main",
                    css: css`
                        width: 20px;
                        height: 18px;
                        svg {
                            fill: ${getColor("tertiary")};
                        }
                    `,
                },
            },
        },
    };
};

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productItem: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 316px;
            padding: 20px;
            position: relative;
            &:hover {
                background-color: #ffffff;
                box-shadow: 0 2px 11px 0 rgba(53, 65, 94, 0.25);
            }
            @media (max-width: 1024px) {
                background-color: #ffffff;
                box-shadow: 0 2px 11px 0 rgba(53, 65, 94, 0.25);
            }
        `,
    },
    productImageStyles: {
        wrapper: {
            css: css`
                position: relative;
                width: 100%;
                margin-bottom: 20px;
            `,
        },
        linkWrappingImage: {
            css: css`
                height: 275px;
                width: 275px;
                padding: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                @media (max-width: 1400px) {
                    &&& {
                        width: 100%;
                    }
                }
            `,
        },
        image: {
            cloudinaryWidth: "275",
            height: "auto",
            css: css`
                img {
                    max-width: 275px;
                    max-height: 275px;
                }
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: fontConfig.fontSize.regular,
                weight: fontConfig.weight.semiBold,
                lineHeight: "17px",
                css: css`
                    letter-spacing: 1px;
                    height: 34px;
                    color: ${getColor("text")};
                `,
            },
            css: css`
                margin-bottom: 24px;
            `,
        },
        productDetailDescription: {
            size: 18,
            lineHeight: "20px",
            css: css`
                opacity: 0.8;
                margin-bottom: ${fontConfig.spacing.heading};
                height: 44px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                border-bottom: 1px solid ${getColor("common.ui2")};
                margin-bottom: 11px;
                height: 21px; // just in case the mfr is not there
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 10px;
            display: flex;
            flex-direction: column;
        `,
    },
    priceStlye: {
        wrapper: {
            css: css`
                width: auto;
            `,
        },
        price: {
            priceText: {
                size: fontConfig.fontSize.small,
                weight: fontConfig.weight.semiBold,
                lineHeight: "17px",
                css: css`
                    letter-spacing: 1px;
                `,
            },
            unitOfMeasureText: {
                size: fontConfig.fontSize.small,
                weight: fontConfig.weight.semiBold,
                transform: "uppercase",
                lineHeight: "17px",
                css: css`
                    white-space: nowrap;
                `,
            },
            nonStockText: {
                css: css`
                    ${productPriceStyles.price?.nonStockText?.css}
                    max-width: 280px;
                `,
            },
        },
        quoteMessage: {
            tooltip: {
                cssOverrides: {
                    tooltipClickable: css`
                        height: 18px;
                    `,
                },
            },
            text: {
                size: fontConfig.fontSize.small,
                weight: fontConfig.weight.semiBold,
                transform: "uppercase",
                lineHeight: "17px",
            },
        },
    },
    productAvailabilityStyles: {
        container: {
            css: css``,
        },
        nonStockText: {
            css: css`
                ${productAvailabilityStyles.nonStockText?.css}
                max-width: 280px;
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 10px;
            `,
        },
    },
    addToCartContainer: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    qtyGridItem: {
        css: css`
            height: 100%;
            > div {
                height: 100%;
            }
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: {
        css: css`
            flex-grow: 1;
            button {
                width: 100%;
            }
            .icon-only {
                width: 85px !important;
            }
        `,
    },
    productAddToListLinkWrapper: {
        css: css`
            position: absolute;
            right: 30px;
            top: 30px;
        `,
    },
    productAddToListLinkStyles: {
        ...getProductAddToListLinkStyles(),
    },
    productAddToListLinkStylesHovered: {
        ...getProductAddToListLinkStyles(true),
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: fontConfig.weight.bold,
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    customActionsTemplate,
    fromShopByLook,
    updateShopByLookProduct,
}: Props) => {
    if (!product || !productInfo) {
        return null;
    }

    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        if (!fromShopByLook) {
            updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
        } else {
            updateShopByLookProduct({ productErp: product.productNumber, qtyOrdered, isQty: true });
        }
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        if (!fromShopByLook) {
            updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
        } else {
            updateShopByLookProduct({ productErp: product.productNumber, uom: unitOfMeasure, isQty: false });
        }
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const [isTouch, setIsTouch] = React.useState(false);

    useEffect(() => {
        // detecting if device is in touch mode to add the dragable ability for product carousel
        isTouchDevice() && setIsTouch(true);
    }, []);

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <StyledWrapper className={`product-card ${isTouch ? "" : "no-swiping-elements"}`} {...styles.productItem}>
            <ProductContext.Provider value={productContext}>
                {customActionsTemplate && (
                    <StyledWrapper {...styles.customActionStyle}>{customActionsTemplate(product)}</StyledWrapper>
                )}
                {showImage && !showOverlayBadge && (
                    <ProductImage
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productImageStyles}
                    />
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper
                        {...styles.badgeAreaWrapper}
                        ref={badgeAreaWrapperRef}
                        data-test-selector="productCarouselBadge"
                    >
                        <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                <ProductDescription
                    lines={2}
                    lineHeight={17}
                    product={productContext}
                    onClick={() => trackProductRecommendation(true)}
                    extendedStyles={styles.productDescriptionStyles}
                />
                {showAddToList && (
                    <StyledWrapper {...styles.productAddToListLinkWrapper}>
                        <ProductAddToListLink data-test-selector="addToListLink" />
                    </StyledWrapper>
                )}
                {showTextBadges && textBadges && (
                    <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                )}
                {showImageBadges && imageBadges && (
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                )}
                {showPrice && (
                    <StyledWrapper {...styles.productPriceWrapper}>
                        <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                            {translate("U/M")}
                        </VisuallyHidden>
                        <ProductPrice
                            showUnitOfMeasure={true}
                            showLabel={false}
                            product={productContext}
                            extendedStyles={styles.priceStlye as any}
                        />
                    </StyledWrapper>
                )}
                {showAddToCart && (
                    <StyledWrapper {...styles.addToCartContainer}>
                        <StyledWrapper {...styles.qtyGridItem}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered />
                        </StyledWrapper>
                        <StyledWrapper {...styles.addToCartButtonGridItem}>
                            <ProductAddToCartButton
                                iconOnly={false}
                                onAddToCartClick={() => trackProductRecommendation(false)}
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </ProductContext.Provider>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
